import { drawImageCover, drawImageContain } from './imageUtils';
import { makeSeededRandom, valueNoise2D } from './noise';

// ---- Shape path builders (all centered at 0,0 for easy rotation) --------
function pathCircle(ctx, r) {
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
}
function pathSquare(ctx, r) {
  ctx.beginPath();
  ctx.rect(-r, -r, r * 2, r * 2);
}
function pathRoundedSquare(ctx, r) {
  const radius = r * 0.28;
  const s = r * 2;
  const x = -r, y = -r;
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + s, y, x + s, y + s, radius);
  ctx.arcTo(x + s, y + s, x, y + s, radius);
  ctx.arcTo(x, y + s, x, y, radius);
  ctx.arcTo(x, y, x + s, y, radius);
  ctx.closePath();
}
function pathHexagon(ctx, r) {
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 3) * i - Math.PI / 2;
    const px = r * Math.cos(angle);
    const py = r * Math.sin(angle);
    if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
  }
  ctx.closePath();
}
function pathStar(ctx, r) {
  const spikes = 5;
  const outer = r;
  const inner = r * 0.42;
  ctx.beginPath();
  for (let i = 0; i < spikes * 2; i++) {
    const radius = i % 2 === 0 ? outer : inner;
    const angle = (Math.PI / spikes) * i - Math.PI / 2;
    const px = radius * Math.cos(angle);
    const py = radius * Math.sin(angle);
    if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
  }
  ctx.closePath();
}

const SHAPE_PATHS = {
  circle: pathCircle,
  square: pathSquare,
  roundedSquare: pathRoundedSquare,
  hexagon: pathHexagon,
  star: pathStar,
};

// ---- Procedural vinyl finishes -------------------------------------------
function fillProceduralVinyl(ctx, r, vinyl) {
  const { color, finish, secondaryColor, seed } = vinyl;
  pathCircle(ctx, r);
  ctx.save();
  ctx.clip();
  ctx.fillStyle = color;
  ctx.fillRect(-r, -r, r * 2, r * 2);

  if (finish === 'splatter') {
    const rand = makeSeededRandom(seed || 7);
    const dotCount = 220;
    for (let i = 0; i < dotCount; i++) {
      const angle = rand() * Math.PI * 2;
      const dist = Math.sqrt(rand()) * r;
      const px = Math.cos(angle) * dist;
      const py = Math.sin(angle) * dist;
      const dotR = 1.5 + rand() * r * 0.045;
      ctx.globalAlpha = 0.55 + rand() * 0.35;
      ctx.fillStyle = rand() > 0.5 ? secondaryColor : '#ffffff';
      ctx.beginPath();
      ctx.arc(px, py, dotR, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  } else if (finish === 'marble') {
    const noise = valueNoise2D(seed || 11);
    const streaks = 5;
    for (let s = 0; s < streaks; s++) {
      ctx.beginPath();
      ctx.strokeStyle = secondaryColor;
      ctx.globalAlpha = 0.18 + (s % 2) * 0.08;
      ctx.lineWidth = r * (0.03 + 0.015 * s);
      const steps = 40;
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const baseAngle = (s / streaks) * Math.PI * 2;
        const n = noise(t * 3 + s, s * 2) - 0.5;
        const dist = t * r;
        const angle = baseAngle + n * 2.4;
        const px = Math.cos(angle) * dist;
        const py = Math.sin(angle) * dist;
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      }
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }
  ctx.restore();
}

function drawGrooves(ctx, r) {
  const inner = r * 0.42;
  const outer = r * 0.97;
  const ringCount = 46;
  ctx.save();
  pathCircle(ctx, r);
  ctx.clip();
  for (let i = 0; i < ringCount; i++) {
    const t = i / ringCount;
    const radius = inner + (outer - inner) * t;
    ctx.beginPath();
    ctx.arc(0, 0, radius, 0, Math.PI * 2);
    ctx.lineWidth = 1;
    ctx.strokeStyle = i % 2 === 0 ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.10)';
    ctx.stroke();
  }
  ctx.restore();
}

function drawSpecularSheen(ctx, cx, cy, r) {
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.clip();
  const grad = ctx.createLinearGradient(cx - r, cy - r, cx + r, cy + r);
  grad.addColorStop(0, 'rgba(255,255,255,0.16)');
  grad.addColorStop(0.35, 'rgba(255,255,255,0.02)');
  grad.addColorStop(0.55, 'rgba(255,255,255,0.0)');
  grad.addColorStop(0.8, 'rgba(255,255,255,0.05)');
  grad.addColorStop(1, 'rgba(255,255,255,0.14)');
  ctx.fillStyle = grad;
  ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
  ctx.restore();
}

// ---- Disc size ratios (fraction of half the smaller frame dimension) -----
const SIZE_RATIOS = { 'CD': 0.55, '7in': 0.62, '10in': 0.75, '12in': 0.86, '16in': 0.95 };

function drawDisc(ctx, disc, cx, cy, frameHalf, timeInSeconds) {
  const rpm = disc.rpm || 33;
  const baseRatio = SIZE_RATIOS[disc.size] || SIZE_RATIOS['12in'];
  const radius = frameHalf * baseRatio * (disc.zoom || 1);
  const rotationDeg = (rpm / 60) * 360 * timeInSeconds;
  const rotationRad = (rotationDeg % 360) * (Math.PI / 180);

  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(rotationRad);

  if (disc.designMode === 'vinyl') {
    fillProceduralVinyl(ctx, radius, disc.vinyl || {});
    drawGrooves(ctx, radius);
    if (disc.imageEl) {
      const labelR = radius * 0.35;
      ctx.save();
      pathCircle(ctx, labelR);
      ctx.clip();
      drawImageContain(ctx, disc.imageEl, 0, 0, labelR * 2);
      ctx.restore();
      ctx.beginPath();
      ctx.arc(0, 0, labelR, 0, Math.PI * 2);
      ctx.lineWidth = Math.max(1, radius * 0.01);
      ctx.strokeStyle = 'rgba(0,0,0,0.35)';
      ctx.stroke();
    }
  } else {
    const pathFn = SHAPE_PATHS[disc.shape] || pathCircle;
    ctx.save();
    pathFn(ctx, radius);
    ctx.clip();
    if (disc.imageEl) {
      drawImageContain(ctx, disc.imageEl, 0, 0, radius * 2);
    } else {
      ctx.fillStyle = '#2a2a2a';
      ctx.fillRect(-radius, -radius, radius * 2, radius * 2);
    }
    ctx.restore();
    ctx.save();
    pathFn(ctx, radius);
    ctx.lineWidth = Math.max(1, radius * 0.015);
    ctx.strokeStyle = 'rgba(0,0,0,0.25)';
    ctx.stroke();
    ctx.restore();
  }

  ctx.restore(); // undo rotation — sheen + spindle stay screen-fixed

  if (disc.designMode === 'vinyl') {
    drawSpecularSheen(ctx, cx, cy, radius);
    if (disc.spindleVisible) {
      ctx.beginPath();
      ctx.arc(cx, cy, Math.max(3, radius * 0.025), 0, Math.PI * 2);
      ctx.fillStyle = '#c9c9c9';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(cx, cy, Math.max(2, radius * 0.015), 0, Math.PI * 2);
      ctx.fillStyle = '#5a5a5a';
      ctx.fill();
    }
  }
}

// ---- The single source-of-truth draw function -----------------------------
// settings = { width, height, background, disc, foreground }
// timeInSeconds is the ONLY thing that determines rotation / animation state.
export function drawFrame(ctx, settings, timeInSeconds) {
  const { width, height, background, disc, foreground } = settings;

  ctx.clearRect(0, 0, width, height);

  // 1. Background
  if (background.type === 'image' && background.imageEl) {
    drawImageCover(ctx, background.imageEl, 0, 0, width, height);
  } else {
    ctx.fillStyle = background.color || '#0f0f10';
    ctx.fillRect(0, 0, width, height);
  }

  // 2 & 3. Disc + spindle
  const cx = width / 2;
  const cy = height / 2;
  const frameHalf = Math.min(width, height) / 2;
  drawDisc(ctx, disc, cx, cy, frameHalf, timeInSeconds);

  // 4. Foreground overlay (full frame, alpha PNG)
  if (foreground && foreground.imageEl) {
    drawImageCover(ctx, foreground.imageEl, 0, 0, width, height);
  }
}

export const DISC_SIZE_RATIOS = SIZE_RATIOS;

// ---- CSS-transform preview architecture -----------------------------------
// The disc's static appearance (shape mask / vinyl texture / label / baked
// spindle) is rendered ONCE per settings change onto a small square canvas.
// Rotation is then applied as a CSS `transform: rotate()` on that canvas
// element directly (no redraw, no React re-render per frame) -- see
// PreviewCanvas.jsx. The sheen is a separate static (non-rotating) layer.
export function drawStaticDiscFace(ctx, disc, sizePx) {
  ctx.clearRect(0, 0, sizePx, sizePx);
  const radius = sizePx / 2;
  ctx.save();
  ctx.translate(radius, radius);
  const r = radius - 2;

  if (disc.designMode === 'vinyl') {
    fillProceduralVinyl(ctx, r, disc.vinyl || {});
    drawGrooves(ctx, r);
    if (disc.imageEl) {
      const labelR = r * 0.35;
      ctx.save();
      pathCircle(ctx, labelR);
      ctx.clip();
      drawImageContain(ctx, disc.imageEl, 0, 0, labelR * 2);
      ctx.restore();
      ctx.beginPath();
      ctx.arc(0, 0, labelR, 0, Math.PI * 2);
      ctx.lineWidth = Math.max(1, r * 0.01);
      ctx.strokeStyle = 'rgba(0,0,0,0.35)';
      ctx.stroke();
    }
    if (disc.spindleVisible) {
      ctx.beginPath();
      ctx.arc(0, 0, Math.max(3, r * 0.025), 0, Math.PI * 2);
      ctx.fillStyle = '#c9c9c9';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(0, 0, Math.max(2, r * 0.015), 0, Math.PI * 2);
      ctx.fillStyle = '#5a5a5a';
      ctx.fill();
    }
  } else {
    const pathFn = SHAPE_PATHS[disc.shape] || pathCircle;
    ctx.save();
    pathFn(ctx, r);
    ctx.clip();
    if (disc.imageEl) {
      drawImageContain(ctx, disc.imageEl, 0, 0, r * 2);
    } else {
      ctx.fillStyle = '#2a2a2a';
      ctx.fillRect(-r, -r, r * 2, r * 2);
    }
    ctx.restore();
    ctx.save();
    pathFn(ctx, r);
    ctx.lineWidth = Math.max(1, r * 0.015);
    ctx.strokeStyle = 'rgba(0,0,0,0.25)';
    ctx.stroke();
    ctx.restore();
  }

  // Center hole -- mimics a real record's spindle hole. 'transparent'
  // punches an actual alpha hole (the background canvas beneath then shows
  // through, whether it's a solid color or an image); 'color' paints a
  // fixed color instead. Applies in both Shape and Vinyl mode.
  if (disc.centerHole && disc.centerHole.enabled) {
    const holeR = r * (disc.centerHole.sizePct / 100);
    if (disc.centerHole.fill === 'color') {
      ctx.beginPath();
      ctx.arc(0, 0, holeR, 0, Math.PI * 2);
      ctx.fillStyle = disc.centerHole.color;
      ctx.fill();
    } else {
      ctx.save();
      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();
      ctx.arc(0, 0, holeR, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }

  ctx.restore();
}

export function drawStaticSheen(ctx, sizePx) {
  ctx.clearRect(0, 0, sizePx, sizePx);
  const radius = sizePx / 2;
  drawSpecularSheen(ctx, radius, radius, radius - 2);
}
